/**
 * The rules a chain formation obeys (`SpatialFormationV1` `CHAIN`), written once so the canonical validator, the
 * composite compiler and the skill language's checker refuse exactly the same chains with exactly the same codes.
 *
 * A chain is one stage whose members go from enemy to enemy: the first is released as the stage would be, each later
 * one from the enemy the chain last struck toward the next enemy it picks. Members are the same stage, not a
 * generation, and each one is one attack unit of the cast.
 */
import type { Delivery } from '../types.js';

/** PROVISIONAL: the shortest jump, the smallest body's diameter (2 x the basic projectile's 0.25 m radius), as craft distances are. */
export const CHAIN_RANGE_MIN_M = 0.5;
/** PROVISIONAL: the longest jump; past it the next enemy is a different fight, and a pick's cost grows with the area it searches. */
export const CHAIN_RANGE_MAX_M = 12;

export type ChainRefusalCode =
  | 'CHAIN_JUMPS_INVALID'
  | 'CHAIN_RANGE_INVALID'
  | 'CHAIN_PICK_INVALID'
  | 'CHAIN_SCHEDULE_UNSUPPORTED'
  | 'CHAIN_GROUPING_UNSUPPORTED'
  | 'CHAIN_MEMBERS_MISMATCH'
  | 'CHAIN_FILTER_UNKNOWN'
  | 'CHAIN_FILTER_ELEMENT'
  | 'CHAIN_FILTER_CARRIER'
  | 'CHAIN_FILTER_TIMING'
  | 'CHAIN_FILTER_MULTIPLE_CREATORS'
  | 'CHAIN_FILTER_CHAIN_CREATOR'
  | 'CHAIN_FILTER_ANCHOR'
  | 'CHAIN_FILTER_STATUS_EXPIRES'
  | 'CHAIN_CARRIER_UNSUPPORTED'
  | 'CHAIN_RANGE_EXCEEDS_CARRIER';

export const CHAIN_REFUSAL_CODES: readonly ChainRefusalCode[] = Object.freeze([
  'CHAIN_JUMPS_INVALID', 'CHAIN_RANGE_INVALID', 'CHAIN_PICK_INVALID', 'CHAIN_SCHEDULE_UNSUPPORTED', 'CHAIN_GROUPING_UNSUPPORTED',
  'CHAIN_MEMBERS_MISMATCH', 'CHAIN_FILTER_UNKNOWN', 'CHAIN_FILTER_ELEMENT', 'CHAIN_FILTER_CARRIER', 'CHAIN_FILTER_TIMING', 'CHAIN_CARRIER_UNSUPPORTED',
  'CHAIN_RANGE_EXCEEDS_CARRIER', 'CHAIN_FILTER_MULTIPLE_CREATORS', 'CHAIN_FILTER_CHAIN_CREATOR', 'CHAIN_FILTER_ANCHOR', 'CHAIN_FILTER_STATUS_EXPIRES',
]);

/** True when a jump range is one the chain may take. */
export function chainRangeIsValid(rangeM: number): boolean {
  return Number.isFinite(rangeM) && rangeM >= CHAIN_RANGE_MIN_M && rangeM <= CHAIN_RANGE_MAX_M;
}

/** The tick rate a fixed tick offset of a program is read at: the one every canonical combat world runs at. */
export const CHAIN_TIMING_TICK_RATE_HZ = 20;

/**
 * How long a creating stage's attack lives after it is released, in ticks, or `undefined` when the program alone does not
 * bound it. A projectile lives its flight (`rangeM / speedMps`), an area its lifetime, a sweep its duration, and an
 * instant attack ends where it is released.
 */
export function deliveryMaxLifeTicks(life: { readonly kind: 'INSTANT' } | { readonly kind: 'PROJECTILE'; readonly rangeM: number; readonly speedMps: number } | { readonly kind: 'LASTING'; readonly durationS: number } | undefined): number | undefined {
  if (life === undefined) return undefined;
  if (life.kind === 'INSTANT') return 0;
  const seconds = life.kind === 'PROJECTILE' ? life.rangeM / life.speedMps : life.durationS;
  return Number.isFinite(seconds) && seconds >= 0 ? Math.ceil(seconds * CHAIN_TIMING_TICK_RATE_HZ) : undefined;
}

/**
 * Whether a filtered chain's first pick comes after its carriers can exist. The creating stage puts the status or mark on
 * a carrier when its attacks strike, so a chain may pick only when it is released
 *
 * - by an event of the creating stage, or of a stage that is itself released by an event of it (`eventOfCreator`: the
 *   creator's attacks have struck by then), or
 * - at a fixed tick strictly after every unit of the creator has ended (`firstReleaseTick > lastEndTick`: its last
 *   release plus its delivery's longest life, `deliveryMaxLifeTicks`; either undefined when the program alone does not
 *   fix it, which then proves nothing).
 *
 * Anything else can release the chain while the creator has created no carrier, and a chain with none cancels whole.
 */
export function chainFilterTimingIsLegal(chain: { readonly eventOfCreator: boolean; readonly firstReleaseTick: number | undefined }, creator: { readonly lastEndTick: number | undefined }): boolean {
  if (chain.eventOfCreator) return true;
  return chain.firstReleaseTick !== undefined && creator.lastEndTick !== undefined && chain.firstReleaseTick > creator.lastEndTick;
}

/** How long a filtered chain's carriers stay carriers once the creating stage has made them. */
export type ChainCarrierLife =
  /** A mark, or a status with neither a duration nor a stage to end it: lives until consumed or until the cast's work is done. */
  | { readonly kind: 'CAST' }
  /** A status of `durationS` seconds, in ticks. */
  | { readonly kind: 'DURATION'; readonly ticks: number }
  /** A status that ends when a stage settles; `settlesAfterChain` is whether that stage provably settles after the chain's last member is released. */
  | { readonly kind: 'UNTIL_STAGE'; readonly settlesAfterChain: boolean };

/**
 * The most ticks from a chain's first release to its last member's release: a planned chain releases every other member
 * when the first strikes (at most `memberLifeTicks` after its release); a ricochet sends each member `intervalTicks` after
 * the previous one strikes. `undefined` when the member's life is not bounded by the program.
 */
export function chainSpanTicks(chain: { readonly count: number; readonly ricochetIntervalTicks: number | undefined; readonly memberLifeTicks: number | undefined }): number | undefined {
  if (chain.memberLifeTicks === undefined) return undefined;
  return chain.ricochetIntervalTicks === undefined ? chain.memberLifeTicks : (chain.count - 1) * (chain.ricochetIntervalTicks + chain.memberLifeTicks);
}

/**
 * Whether the carriers a filtered chain picks among are guaranteed alive until its last member can release. `gapTicks` is
 * how long after the creating stage's last attack has ended that release can be (`undefined` when the program does not fix
 * it, which proves nothing); a duration must exceed it.
 */
export function chainCarriersSurvive(life: ChainCarrierLife, gapTicks: number | undefined): boolean {
  if (life.kind === 'CAST') return true;
  if (life.kind === 'UNTIL_STAGE') return life.settlesAfterChain;
  return gapTicks !== undefined && life.ticks > gapTicks;
}

/** The ticks a status of `durationS` seconds lasts at the program's tick rate. */
export function chainStatusDurationTicks(durationS: number): number {
  return Math.ceil(durationS * CHAIN_TIMING_TICK_RATE_HZ);
}

/** True when a chain of `count` nodes fits the cast's attack units (`maxAttackUnits`): each node is one member, one unit. */
export function chainCountIsValid(count: number, maxAttackUnits: number): boolean {
  return Number.isInteger(count) && count >= 2 && count <= maxAttackUnits;
}

/** What a chain's member carries, as the runtime places it at a node: a dash moves the caster to the node and strikes there. */
export type ChainCarrier = 'PROJECTILE' | 'AREA' | 'CONE' | 'DASH';

export type ChainCarrierResult =
  | { readonly ok: true; readonly carrier: ChainCarrier }
  | { readonly ok: false; readonly code: ChainRefusalCode; readonly message: string };

/**
 * Whether a stage's lowered delivery can carry a chain, and as what.
 *
 * - A projectile, an area at a resolved point (instant or persistent) and a wedge each place at a node: a projectile flies
 *   from the last node toward the next, an area lands on the next node, a wedge opens from the last node toward the next.
 * - A weapon's own contact (a swing or a thrust) is bound to the caster's body, which stays where it is: it has nothing to
 *   carry from one enemy to the next. A circle at the caster (orbs included) is centred on the caster for its whole life.
 *   Anything else (an attack on the aimed enemy alone, a self cast) has no geometry to place at a node.
 * - A member must reach the node it is aimed at: a projectile's range or a wedge's reach shorter than the jump would
 *   strike nothing at the end of a long jump.
 * - With `only`, a projectile is refused: it can strike an enemy without the status before it reaches its goal.
 * - A dash stage's circle at the caster is carried by the caster itself, who moves to each node and strikes there; how far
 *   it can jump is the dash's own rule (`DASH_CHAIN_RANGE_EXCEEDS_DASH`).
 */
export function chainCarrier(delivery: Delivery, weaponContact: boolean, jumpRangeM: number, filtered: boolean, dash = false): ChainCarrierResult {
  const refuse = (code: ChainRefusalCode, message: string): ChainCarrierResult => ({ ok: false, code, message });
  if (dash) return { ok: true, carrier: 'DASH' };
  if (weaponContact) return refuse('CHAIN_CARRIER_UNSUPPORTED', 'a weapon\'s own contact is bound to the caster\'s body, so it cannot be carried from one enemy to the next');
  if (delivery.type === 'ground_aoe') {
    if (delivery.center === 'CASTER') return refuse('CHAIN_CARRIER_UNSUPPORTED', 'a circle at the caster stays centred on the caster, so it cannot be carried from one enemy to the next');
    return { ok: true, carrier: 'AREA' };
  }
  if (delivery.type === 'projectile') {
    if (filtered) return refuse('CHAIN_FILTER_CARRIER', 'a projectile can strike an enemy without the status before it reaches its goal: a filtered chain needs an area, a wedge or a dash');
    if (delivery.rangeM < jumpRangeM) return refuse('CHAIN_RANGE_EXCEEDS_CARRIER', `the projectile flies ${delivery.rangeM} m, shorter than the ${jumpRangeM} m jump`);
    return { ok: true, carrier: 'PROJECTILE' };
  }
  if (delivery.type === 'cone') {
    if (delivery.rangeM < jumpRangeM) return refuse('CHAIN_RANGE_EXCEEDS_CARRIER', `the wedge reaches ${delivery.rangeM} m, shorter than the ${jumpRangeM} m jump`);
    return { ok: true, carrier: 'CONE' };
  }
  return refuse('CHAIN_CARRIER_UNSUPPORTED', `a ${delivery.type} delivery has no geometry to place at an enemy of the chain`);
}
